import { SupplierPayments } from "@/domain/supplier-payments";
import { FinancePresentation } from "@/presentation/finance";

type SupplierPaymentSummary = ReturnType<typeof SupplierPayments.summarize>[number];

export function SupplierBarChart({
	items,
	title,
}: {
	items: SupplierPaymentSummary[];
	title: string;
}) {
	const maximumAmountCents = items.at(0)?.amountCents ?? 0;
	const totalAmountCents = items.reduce((total, item) => total + item.amountCents, 0);

	return (
		<section
			aria-labelledby="supplier-chart-title"
			className="mb-6 overflow-hidden rounded-[1.75rem] border border-stone-200 bg-white shadow-[0_20px_60px_-35px_rgba(28,25,23,0.35)]"
		>
			<div className="flex flex-col justify-between gap-3 border-b border-stone-200 px-5 py-4 sm:flex-row sm:items-end sm:px-6">
				<div>
					<p className="text-xs font-medium text-stone-500">Visão mensal</p>
					<h2 id="supplier-chart-title" className="mt-1 text-lg font-semibold text-stone-950">
						{title}
					</h2>
					<p className="mt-1 text-xs text-stone-500">
						Saídas reconhecidas no extrato, agrupadas pela identidade do fornecedor.
					</p>
				</div>
				<div className="sm:text-right">
					<p className="text-xs text-stone-500">Total exibido</p>
					<p className="mt-1 text-xl font-semibold tabular-nums text-stone-950">
						{FinancePresentation.money(totalAmountCents)}
					</p>
				</div>
			</div>

			<div className="space-y-4 p-5 sm:p-6">
				{items.map((item) => (
					<div
						key={item.supplierId}
						className="grid gap-2 sm:grid-cols-[minmax(160px,240px)_minmax(180px,1fr)_150px] sm:items-center sm:gap-4"
					>
						<div className="min-w-0">
							<p className="truncate text-sm font-semibold text-stone-800">{item.supplierName}</p>
							<p className="mt-0.5 text-[11px] text-stone-400">{item.categoryName}</p>
						</div>
						<div className="h-3 overflow-hidden rounded-full bg-stone-100" aria-hidden="true">
							<div
								className={`h-full rounded-full ${
									item.categoryId === "sugar"
										? "bg-amber-500"
										: item.categoryId === "packaging"
											? "bg-violet-600"
											: "bg-sky-600"
								}`}
								style={{
									width: `${maximumAmountCents === 0 ? 0 : Math.max((item.amountCents / maximumAmountCents) * 100, 1.5)}%`,
								}}
							/>
						</div>
						<div className="flex items-baseline justify-between gap-3 sm:block sm:text-right">
							<p className="text-sm font-semibold tabular-nums text-stone-950">
								{FinancePresentation.money(item.amountCents)}
							</p>
							<p className="text-[11px] text-stone-400">
								{item.transactionCount} {item.transactionCount === 1 ? "pagamento" : "pagamentos"}
							</p>
						</div>
					</div>
				))}
			</div>
		</section>
	);
}
